import { useCallback, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import { faClone } from "@fortawesome/free-solid-svg-icons/faClone";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import {
    LOGIN_HINT_DOCUMENTATION_SHORTNAME,
    OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID
} from "@constants/oidc-login-hint";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { LoginHintSecurityMethodTypes } from "@enums/login-hint-security-methode-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { SelectOption } from "@idw-types/common";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { getJWTKeys } from "@utils/login-hint/jwt-encrypted-util";
import {
    getLoginHintInfo,
    getSelectedLoginHintSecurityMethod
} from "@utils/login-hint/login-hint-utils";
import { ContentPageHeader } from "../../content-page-header/content-page-header";
import { ContentPageBody } from "../../content-page-body/content-page-body";
import { PropertiesSectionDescription } from "../properties-section-header/properties-section-description";
import { ButtonWithIcon } from "../../button-with-icon/button-with-icon";
import { CopyToClipboardWrapper } from "../../copy-to-clipboard-wrapper/copy-to-clipboard-wrapper";
import { ThemingFormGroupWrapper } from "../../theming-input-form-group/theming-form-group-wrapper";
import styles from './login-hint-container.less';
import { LoginHintExpirationContainer } from "@containers/common/properties-sections/login-hint-container/login-hint-expiration-container/login-hint-expiration-container";

type LoginHintContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    appliedProfiles: Array<FormattedAppliedProfile>;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    documentationLinks: Array<AxnDocumentationLink>;
    pageLevel: PageLevelTypes;
    workflowId: string;
    canModifyNonRestrictedProperties: boolean;
    onChangeEnableLoginHint: (newValue: string) => void;
    onChangeLoginHintSecurityMethod: (newValue: LoginHintSecurityMethodTypes) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
};

export const LoginHintContainer = (props: LoginHintContainerProps) => {
    const {
        appliedProfiles,
        properties,
        validationErrors,
        documentationLinks,
        pageLevel,
        workflowId,
        canModifyNonRestrictedProperties,
        onChangeEnableLoginHint,
        onChangeLoginHintSecurityMethod,
        updateProperties
    } = props;

    const [isPrivateKeyVisible, setIsPrivateKeyVisible] = useState(false);
    const [isPublicKeyVisible, setIsPublicKeyVisible] = useState(false);

    const loginHintResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.loginHint;

    const isDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            (pageLevel === PageLevelTypes.APIKEY && !!workflowId) ||
            !canModifyNonRestrictedProperties
        );
    }, [props.isDisabled, pageLevel, workflowId, canModifyNonRestrictedProperties]);

    const isServiceWithoutWorkflow = useMemo(() => {
        return pageLevel === PageLevelTypes.APIKEY && !workflowId;
    }, [pageLevel, workflowId]);

    const isResetDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            pageLevel === PageLevelTypes.WORKFLOW ||
            isServiceWithoutWorkflow ||
            !canModifyNonRestrictedProperties
        );
    }, [props.isDisabled, pageLevel, isServiceWithoutWorkflow, canModifyNonRestrictedProperties]);

    const loginHintInfo = useMemo(() => {
        return getLoginHintInfo(properties, appliedProfiles);
    }, [properties, appliedProfiles]);

    const inheritedLoginHintInfo = useMemo(() => {
        return getLoginHintInfo([], appliedProfiles);
    }, [appliedProfiles]);

    const selectedLoginHintSecurityMethod = useMemo(() => {
        return getSelectedLoginHintSecurityMethod(
            loginHintInfo.isOidcTokenUserAttributeEnabled,
            loginHintInfo.isWTRSAPublicKeyEnabled,
            loginHintInfo.isWTRSAPrivateKeyEnabled,
            loginHintInfo.isLoginHintJWTEnabled
        );
    }, [
        loginHintInfo.isOidcTokenUserAttributeEnabled,
        loginHintInfo.isWTRSAPublicKeyEnabled,
        loginHintInfo.isWTRSAPrivateKeyEnabled,
        loginHintInfo.isLoginHintJWTEnabled
    ]);

    const inheritedSelectedLoginHintSecurityMethod = useMemo(() => {
        return getSelectedLoginHintSecurityMethod(
            inheritedLoginHintInfo.isOidcTokenUserAttributeEnabled,
            inheritedLoginHintInfo.isWTRSAPublicKeyEnabled,
            inheritedLoginHintInfo.isWTRSAPrivateKeyEnabled,
            inheritedLoginHintInfo.isLoginHintJWTEnabled
        );
    }, [
        inheritedLoginHintInfo.isOidcTokenUserAttributeEnabled,
        inheritedLoginHintInfo.isWTRSAPublicKeyEnabled,
        inheritedLoginHintInfo.isWTRSAPrivateKeyEnabled,
        inheritedLoginHintInfo.isLoginHintJWTEnabled
    ]);

    const { loginHintJwtEncryptedPublicKey, loginHintJwtEncryptedPrivateKey } = useMemo(() => {
        return {
            loginHintJwtEncryptedPublicKey: loginHintInfo.tRSAPublicKeyPropertyValue || '',
            loginHintJwtEncryptedPrivateKey: loginHintInfo.tRSAPrivateKeyPropertyValue || ''
        }
    }, [
        loginHintInfo.tRSAPublicKeyPropertyValue,
        loginHintInfo.tRSAPrivateKeyPropertyValue
    ]);

    const { inheritedLoginHintJwtEncryptedPublicKey, inheritedLoginHintJwtEncryptedPrivateKey } = useMemo(() => {
        return {
            inheritedLoginHintJwtEncryptedPublicKey: inheritedLoginHintInfo.tRSAPublicKeyPropertyValue || '',
            inheritedLoginHintJwtEncryptedPrivateKey: inheritedLoginHintInfo.tRSAPrivateKeyPropertyValue || ''
        }
    }, [
        inheritedLoginHintInfo.tRSAPublicKeyPropertyValue,
        inheritedLoginHintInfo.tRSAPrivateKeyPropertyValue
    ]);

    const onChangeLoginHintSecurityMethodInner = (value: LoginHintSecurityMethodTypes) => {
        setIsPrivateKeyVisible(false);
        setIsPublicKeyVisible(false);
        onChangeLoginHintSecurityMethod(value);
    };

    const handleResetLoginHintSecurityMethod = () => {
        updateProperties([
            { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false }
        ]);
    };

    const generateKeys = async () => {
        const jwtKeys = await getJWTKeys();
        if (jwtKeys) {
            updateProperties([
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: jwtKeys.publicKey, added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: jwtKeys.privateKey, added: true }
            ]);
        }
    }

    const clearKeys = () => {
        updateProperties([
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: "", added: true },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: "", added: true }
        ]);
    };

    const onChangeEnableLoginHintInner = (newValue: string) => {
        setIsPrivateKeyVisible(false);
        setIsPublicKeyVisible(false);

        onChangeEnableLoginHint(newValue);
    };

    const handleResetEnableLoginHint = () => {
        updateProperties([
            { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false }
        ]);
    };

    const handleResetLoginHintJwtEncryptedKeys = () => {
        updateProperties([
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false }
        ]);
    };

    const onClickViewPrivateKey = useCallback(() => {
        setIsPrivateKeyVisible(!isPrivateKeyVisible);
    }, [isPrivateKeyVisible, setIsPrivateKeyVisible]);

    const onClickViewPublicKey = useCallback(() => {
        setIsPublicKeyVisible(!isPublicKeyVisible);
    }, [isPublicKeyVisible, setIsPublicKeyVisible]);

    const loginHintOptions: Array<SelectOption> = useMemo(() => {
        return [
            { label: loginHintResources.inputs.loginHintPlainJSON.label, value: LoginHintSecurityMethodTypes.PLAIN_JSON },
            { label: loginHintResources.inputs.loginHintJWTSigned.label, value: LoginHintSecurityMethodTypes.SIGNED_JWT },
            { label: loginHintResources.inputs.loginHintJWTEncrypted.label, value: LoginHintSecurityMethodTypes.JWT_ENCRYPTED }
        ];
    }, []);

    const loginHintTooltips: Array<string> = useMemo(() => {
        return [
            loginHintResources.inputs.loginHintPlainJSON.tooltipText,
            loginHintResources.inputs.loginHintJWTSigned.tooltipText,
            loginHintResources.inputs.loginHintJWTEncrypted.tooltipText
        ];
    }, []);

    const publicKeyText = useMemo(() => {
        if (isPublicKeyVisible) {
            return loginHintJwtEncryptedPublicKey || "";
        } else if (loginHintJwtEncryptedPublicKey) {
            return "XXXXXXXXXXXX";
        }
        return "";
    }, [isPublicKeyVisible, loginHintJwtEncryptedPublicKey]);

    const privateKeyText = useMemo(() => {
        if (isPrivateKeyVisible) {
            return loginHintJwtEncryptedPrivateKey || "";
        } else if (loginHintJwtEncryptedPrivateKey) {
            return "XXXXXXXXXXXX";
        }
        return "";
    }, [isPrivateKeyVisible, loginHintJwtEncryptedPrivateKey]);

    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === LOGIN_HINT_DOCUMENTATION_SHORTNAME);
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);

    const privateKeyAfterInputElement = useMemo(() => {
        return (
            <>
                <ButtonWithIcon
                    icon={isPrivateKeyVisible ? faEyeSlash : faEye}
                    title={loginHintResources.buttons.viewPrivateKey.title}
                    onClick={onClickViewPrivateKey}
                    variant={"secondary"}
                    id={`login-hint-view-private-key-button`}
                    disabled={!loginHintJwtEncryptedPrivateKey}
                />
                <CopyToClipboardWrapper
                    title={loginHintResources.buttons.copyPrivateKey.title}
                    disabled={!isPrivateKeyVisible}
                    icon={faClone}
                    variant={"secondary"}
                    placement={"top-end"}
                    id={`login-hint-copy-private-key-button`}
                    text={loginHintJwtEncryptedPrivateKey}
                />
            </>
        )
    }, [isPrivateKeyVisible, loginHintJwtEncryptedPrivateKey, onClickViewPrivateKey]);

    const publicKeyAfterInputElement = useMemo(() => {
        return (
            <>
                <ButtonWithIcon
                    icon={isPublicKeyVisible ? faEyeSlash : faEye}
                    title={loginHintResources.buttons.viewPublicKey.title}
                    onClick={onClickViewPublicKey}
                    variant={"secondary"}
                    id={`login-hint-view-public-key-button`}
                    disabled={!loginHintJwtEncryptedPublicKey}
                />
                <CopyToClipboardWrapper
                    title={loginHintResources.buttons.copyPublicKey.title}
                    disabled={!isPublicKeyVisible}
                    icon={faClone}
                    variant={"secondary"}
                    placement={"top-end"}
                    id={`login-hint-copy-public-key-button`}
                    text={loginHintJwtEncryptedPublicKey}
                />
            </>
        );
    }, [isPublicKeyVisible, loginHintJwtEncryptedPublicKey, onClickViewPublicKey]);

    const isLoginHintResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            loginHintInfo.isOidcTokenUserAttributeEnabled === inheritedLoginHintInfo.isOidcTokenUserAttributeEnabled
        );
    }, [
        isResetDisabled,
        loginHintInfo.isOidcTokenUserAttributeEnabled,
        inheritedLoginHintInfo.isOidcTokenUserAttributeEnabled
    ]);

    const isLoginHintSecurtyMethodResetDisabled = useMemo(() => {
        return (
            isResetDisabled
            || inheritedSelectedLoginHintSecurityMethod === null
            || inheritedSelectedLoginHintSecurityMethod === selectedLoginHintSecurityMethod
        );
    }, [
        isResetDisabled,
        selectedLoginHintSecurityMethod,
        inheritedSelectedLoginHintSecurityMethod
    ]);

    const isLoginHintKeysResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            (
                loginHintJwtEncryptedPrivateKey === inheritedLoginHintJwtEncryptedPrivateKey &&
                loginHintJwtEncryptedPublicKey === inheritedLoginHintJwtEncryptedPublicKey
            ) ||
            (
                inheritedLoginHintJwtEncryptedPrivateKey === "" &&
                inheritedLoginHintJwtEncryptedPublicKey === ""
            )
        );
    }, [
        isResetDisabled,
        loginHintJwtEncryptedPrivateKey,
        inheritedLoginHintJwtEncryptedPrivateKey,
        loginHintJwtEncryptedPublicKey,
        inheritedLoginHintJwtEncryptedPublicKey
    ]);

    return (
        <div>
            <ContentPageHeader title={loginHintResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <PropertiesSectionDescription description={loginHintResources.description} documentationLink={documentationLink} />
                <div className={styles.loginHintContent}>
                    <ThemingFormGroupWrapper
                        currentThemingPropertyValue={loginHintInfo.isOidcTokenUserAttributeEnabled}
                        inheritedThemingPropertyValue={inheritedLoginHintInfo.isOidcTokenUserAttributeEnabled}
                        pageLevel={pageLevel}
                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                    >
                        <RadioInputFormGroup
                            controlId={'enable-login-hint'}
                            automationId={'enable-login-hint'}
                            disabled={isDisabled}
                            isRequired={false}
                            label={loginHintResources.inputs.enableLoginHint.label}
                            layout={LayoutTypes.COLUMN}
                            labelWidth={LabelWidthTypes.LARGE}
                            value={loginHintInfo.isOidcTokenUserAttributeEnabled
                                ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value
                                : BOOLEAN_RADIO_INPUT_OPTIONS[1].value
                            }
                            options={BOOLEAN_RADIO_INPUT_OPTIONS}
                            onChange={onChangeEnableLoginHintInner}
                            afterLabelIcon={isLoginHintResetDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetEnableLoginHint}
                        />
                    </ThemingFormGroupWrapper>
                    {loginHintInfo.isOidcTokenUserAttributeEnabled && (
                        <>
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={selectedLoginHintSecurityMethod}
                                inheritedThemingPropertyValue={inheritedSelectedLoginHintSecurityMethod}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <RadioInputFormGroup
                                    isRequired={false}
                                    automationId={`login-hint-security-method`}
                                    controlId={`login-hint-security-method`}
                                    label={loginHintResources.inputs.securityMethod.label}
                                    onChange={onChangeLoginHintSecurityMethodInner}
                                    value={selectedLoginHintSecurityMethod}
                                    disabled={isDisabled}
                                    options={loginHintOptions}
                                    optionTooltips={loginHintTooltips}
                                    layout={LayoutTypes.COLUMN}
                                    radioButtonLayout={LayoutTypes.COLUMN}
                                    afterLabelIcon={isLoginHintSecurtyMethodResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetLoginHintSecurityMethod}
                                />
                            </ThemingFormGroupWrapper>
                            {selectedLoginHintSecurityMethod === LoginHintSecurityMethodTypes.JWT_ENCRYPTED && (
                                <div className={styles.encryptedJwtContainer}>
                                    <div className={styles.encryptedJwtInputGroup}>
                                        <ThemingFormGroupWrapper
                                            currentThemingPropertyValue={loginHintJwtEncryptedPrivateKey}
                                            inheritedThemingPropertyValue={inheritedLoginHintJwtEncryptedPrivateKey}
                                            pageLevel={pageLevel}
                                            hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                        >
                                            <InputFormGroup
                                                controlId={'encrypted-jwt-private-key'}
                                                automationId={'encrypted-jwt-private-key'}
                                                className={styles.encryptedJwtInput}
                                                label={loginHintResources.inputs.loginHintJWTEncryptedPrivateKey.label}
                                                ariaLabel={loginHintResources.inputs.loginHintJWTEncryptedPrivateKey.label}
                                                onChange={() => { }}
                                                readOnly={true}
                                                value={privateKeyText}
                                                disabled={isDisabled || (!isDisabled && loginHintJwtEncryptedPrivateKey !== '')}
                                                isRequired={selectedLoginHintSecurityMethod === LoginHintSecurityMethodTypes.JWT_ENCRYPTED}
                                                layout={LayoutTypes.COLUMN}
                                                labelWidth={LabelWidthTypes.MEDIUM}
                                                afterInputElement={privateKeyAfterInputElement}
                                                afterLabelIcon={isLoginHintKeysResetDisabled ? null : faUndo}
                                                afterLabelIconOnClick={handleResetLoginHintJwtEncryptedKeys}
                                            />
                                        </ThemingFormGroupWrapper>
                                    </div>
                                    {validationErrors.loginHintPrivateKeyEmptyError}

                                    <div className={styles.encryptedJwtInputGroup}>
                                        <ThemingFormGroupWrapper
                                            currentThemingPropertyValue={loginHintJwtEncryptedPublicKey}
                                            inheritedThemingPropertyValue={inheritedLoginHintJwtEncryptedPublicKey}
                                            pageLevel={pageLevel}
                                            hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                        >
                                            <InputFormGroup
                                                controlId={'encrypted-jwt-public-key'}
                                                automationId={'encrypted-jwt-public-key'}
                                                label={loginHintResources.inputs.loginHintJWTEncryptedPublicKey.label}
                                                ariaLabel={loginHintResources.inputs.loginHintJWTEncryptedPublicKey.label}
                                                className={styles.encryptedJwtInput}
                                                onChange={() => { }}
                                                readOnly={true}
                                                value={publicKeyText}
                                                disabled={isDisabled || (!isDisabled && loginHintJwtEncryptedPublicKey !== '')}
                                                isRequired={selectedLoginHintSecurityMethod === LoginHintSecurityMethodTypes.JWT_ENCRYPTED}
                                                layout={LayoutTypes.COLUMN}
                                                labelWidth={LabelWidthTypes.MEDIUM}
                                                afterInputElement={publicKeyAfterInputElement}
                                                afterLabelIcon={isLoginHintKeysResetDisabled ? null : faUndo}
                                                afterLabelIconOnClick={handleResetLoginHintJwtEncryptedKeys}
                                            />
                                        </ThemingFormGroupWrapper>
                                    </div>
                                    {validationErrors.loginHintPublicKeyEmptyError}
                                    {!isDisabled && (
                                        <div className={styles.encryptedJwtKeysButtonGroup}>
                                            <Button
                                                id="jwt-encrypted-generate-keys"
                                                variant="secondary"
                                                onClick={generateKeys}
                                                className={styles.encryptedJwtButton}>
                                                {loginHintResources.buttons.generateKeys.label}
                                            </Button>
                                            <Button
                                                id="jwt-encrypted-clear-keys"
                                                variant="secondary"
                                                disabled={loginHintJwtEncryptedPrivateKey === '' && loginHintJwtEncryptedPublicKey === ''}
                                                onClick={clearKeys}
                                                className={styles.encryptedJwtButton}>
                                                {loginHintResources.buttons.clearKeys.label}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(selectedLoginHintSecurityMethod === LoginHintSecurityMethodTypes.JWT_ENCRYPTED
                                || selectedLoginHintSecurityMethod === LoginHintSecurityMethodTypes.SIGNED_JWT) && (<LoginHintExpirationContainer
                                    isDisabled={isDisabled}
                                    loginHintInfo={loginHintInfo}
                                    loginHintExpirationTypeError={validationErrors.loginHintExpirationTypeError}
                                    loginHintExpirationValueError={validationErrors.loginHintExpirationValueError}
                                    updateProperties={updateProperties}
                                />)}
                        </>
                    )}
                </div>
            </ContentPageBody>
        </div >
    );
}